export function formatNumberWithSeparator(props: {
  value: number | string;
  thousandsSeparator: string;
  decimalSeparator?: string;
  alwaysTwoDecimals?: boolean;
}): string {
  const {
    value,
    thousandsSeparator,
    decimalSeparator = '.',
    alwaysTwoDecimals = false,
  } = props;

  const splitNumber = (val: number | string): [string, string?] => {
    if (alwaysTwoDecimals) {
      const numValue = typeof val === 'string' ? parseFloat(val) : val;

      if (isNaN(numValue)) return [val.toString()];

      const fixed = numValue.toFixed(2);
      const [intPart = '', decPart] = fixed.split('.');
      return [intPart, decPart];
    } else {
      const str = typeof val === 'string' ? val : val.toString();
      const [intPart = '', decPart] = str.split('.');
      return [intPart, decPart];
    }
  };

  let integerPart: string;
  let decimalPart: string | undefined;

  if (typeof value === 'string') {
    // If a number is input in the format "1,500"
    const normalized = value.replace(
      new RegExp(`\\${thousandsSeparator}`, 'g'),
      '',
    );
    [integerPart, decimalPart] = splitNumber(normalized);
  } else {
    [integerPart, decimalPart] = splitNumber(value);
  }

  if (thousandsSeparator && integerPart) {
    integerPart = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      thousandsSeparator,
    );
  }

  if (!decimalPart) {
    return integerPart;
  }

  return `${integerPart}${decimalSeparator}${decimalPart}`;
}

/** A whole amount with its currency and thousands separators: "S$8,000". */
export const formatCurrencyAmount = (value: number, currency: string): string =>
  `${currency}${formatNumberWithSeparator({ value, thousandsSeparator: ',' })}`;

export const currencyFormat = (currency?: string): string => {
  switch (currency) {
    case undefined:
      return '';
    case 'SGD$':
      return 'S$';
    case 'HKD$':
      return 'HK$';
    case 'NT$':
      return 'NT$';
    default:
      return currency;
  }
};

const parseNumber = (v: string | number): number | null => {
  if (typeof v === 'number') return v;
  if (v == null) return null;
  const cleaned = String(v)
    .trim()
    .replace(',', '.')
    .replace(/[^0-9.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

export const formatPercent = (v: string | number): string => {
  const n = parseNumber(v);
  if (n === null) return String(v);

  // Up to 3 decimal places (CMS-1750 review): "0.121" renders as 0.121%
  // instead of 0.1%; trailing zeros are dropped (12.500 -> 12.5%).
  return `${Number(n.toFixed(3))}%`;
};

export function toNonNegativeInt(
  value: number | undefined,
): number | undefined {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return undefined;
  }
  const int = Math.floor(value);
  return int >= 0 ? int : undefined;
}

export function toPositiveInt(value: number | undefined): number | undefined {
  const int = toNonNegativeInt(value);
  return int && int > 0 ? int : undefined;
}
